import React, { ReactNode } from 'react';
import { isMacOs } from 'flow/utils/os-utils-flow';
import { twMerge } from 'tailwind-merge';

interface KeySequenceProps {
    shortcut: string;
}

const keyIconMap: Record<string, ReactNode> = {
    '\\u21E7': <span>⇧</span>, // Shift
    '\\u2325': <span>⌥</span>, // Alt/Option
    '\\u2303': <span>⌃</span>, // Mac Ctrl
    '\\u23CE': <span>↵</span>, // Enter
    '\\u232B': <span>⌫</span>, // Backspace
    '\\u2326': <span>⌦</span>, // Delete
    '\\u238B': <span>⎋</span>, // Escape
    '\\u2423': <span>⎵</span>, // Space
    '\\u2191': <span>↑</span>, // Up arrow
    '\\u2193': <span>↓</span>, // Down arrow
    '\\u2190': <span>←</span>, // Left arrow
    '\\u2192': <span>→</span>, // Right arrow
    '\\u2196': <span>↖</span>, // Home
    '\\u2198': <span>↘</span>, // End
    '\\u21DE': <span>⇞</span>, // Page up
    '\\u21DF': <span>⇟</span>, // Page down
    '\\u21E5': <span>⇥</span>, // Tab
    AltGraph: <span>AltGr</span>, // AltGraph

    // Special case for cmdorctrl
    cmdorctrl: isMacOs() ? <span>⌘</span> : <span>Ctrl</span>,
};

interface SingleKeyBoxProps {
    children: ReactNode;
}

const SingleKeyBox = ({ children }: SingleKeyBoxProps) => (
    <span
        className={twMerge(
            'inline-flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-gray-100 px-2 text-sm text-gray-700 select-none',
            typeof children === 'string' && children.length > 2 ? 'w-auto' : '',
        )}
    >
        {children}
    </span>
);

const KeySequence = ({ shortcut }: KeySequenceProps) => {
    const parts = shortcut?.split(' ');
    return (
        <span className="flex items-center gap-x-2">
            {parts?.map((part, i) => {
                const key = part.trim();
                let displayKey: ReactNode;

                if (key.toLowerCase() === 'cmdorctrl') {
                    displayKey = keyIconMap['cmdorctrl'];
                } else if (keyIconMap[key]) {
                    // First check if it's a special symbol
                    displayKey = keyIconMap[key];
                } else {
                    // Convert Unicode escape to actual character, then uppercase
                    const convertedKey = key.replace(
                        /\\u([0-9a-fA-F]{4})/g,
                        (match, code) => {
                            return String.fromCharCode(parseInt(code, 16));
                        },
                    );
                    displayKey = convertedKey.toUpperCase();
                }

                return (
                    <SingleKeyBox key={`${part}-${i}`}>
                        {displayKey}
                    </SingleKeyBox>
                );
            })}
        </span>
    );
};

export default KeySequence;
